// Pure math helpers for the dimming-profile SVG editor: snapping to the
// 10-minute grid, clamping levels, sorting steps, and formatting time.

import {
  LEVEL_INACTIVE,
  MAX_LEVEL,
  MAX_TIME_MINUTES,
  MIN_LEVEL,
  MIN_TIME_MINUTES,
  ProfileStep,
  TIME_STEP_MINUTES,
} from '../types/dimmingProfile.types'

// Snap an arbitrary minute value onto the 10-minute grid and clamp to range.
export function snapTimeMinutes(minutes: number): number {
  const snapped = Math.round(minutes / TIME_STEP_MINUTES) * TIME_STEP_MINUTES
  return Math.min(MAX_TIME_MINUTES, Math.max(MIN_TIME_MINUTES, snapped))
}

// Clamp a dimming level to 0..100. Inactive (0xFF) is preserved as-is.
export function clampLevel(level: number): number {
  if (level === LEVEL_INACTIVE) return LEVEL_INACTIVE
  return Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(level)))
}

export function isInactive(level: number): boolean {
  return level === LEVEL_INACTIVE
}

// Sort steps ascending by step_time (the encoder also sorts; we keep the UI in
// the same order to avoid surprising the operator).
export function sortSteps(steps: ProfileStep[]): ProfileStep[] {
  return [...steps].sort((a, b) => a.step_time_minutes - b.step_time_minutes)
}

// Format minutes-since-midnight as "HH:MM".
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.min(MAX_TIME_MINUTES, Math.round(minutes)))
  const hh = Math.floor(m / 60)
  const mm = m % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

// Whether a candidate time would collide with an existing step (same grid slot),
// optionally ignoring the step at `ignoreIndex` (the one being dragged).
export function hasTimeCollision(
  steps: ProfileStep[],
  timeMinutes: number,
  ignoreIndex?: number,
): boolean {
  return steps.some(
    (s, i) => i !== ignoreIndex && s.step_time_minutes === timeMinutes,
  )
}
